import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Storage as DatabaseIcon,
  Memory as RedisIcon,
  CloudQueue as KubernetesIcon,
  CloudDone as CloudIcon,
  Security as FirebaseIcon,
  Email as EmailIcon,
  Psychology as AIIcon,
  Dns as PineconeIcon,
} from '@mui/icons-material';
import { systemAPI, ServiceStatusResponse, ServiceStatus } from '../services/api';

const serviceIcons: Record<string, React.ReactNode> = {
  database: <DatabaseIcon />,
  redis: <RedisIcon />,
  kubernetes: <KubernetesIcon />,
  pinecone: <PineconeIcon />,
  azure_storage: <CloudIcon />,
  firebase: <FirebaseIcon />,
  openrouter: <AIIcon />,
  email: <EmailIcon />,
};

const ServiceConfigStatus: React.FC = () => {
  const [serviceStatus, setServiceStatus] = useState<ServiceStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [, setTick] = useState(0); // Force re-render for relative time updates

  const fetchServiceStatus = useCallback(async () => {
    try {
      setLoading(true);
      const data = await systemAPI.getServiceStatus();
      setServiceStatus(data);
      setLastUpdated(new Date());
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch service status:', err);
      setError(err.response?.data?.detail || 'Failed to load service configuration status');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch and auto-refresh every 30 seconds
  useEffect(() => {
    fetchServiceStatus();
    const interval = setInterval(fetchServiceStatus, 30000);
    return () => clearInterval(interval);
  }, [fetchServiceStatus]);

  // Update relative time display every 10 seconds
  useEffect(() => {
    const tickInterval = setInterval(() => setTick(t => t + 1), 10000);
    return () => clearInterval(tickInterval);
  }, []);

  const getOverallStatusColor = (status: string): 'success' | 'warning' | 'error' | 'info' => {
    switch (status) {
      case 'fully_configured':
        return 'success';
      case 'operational':
        return 'info';
      case 'partial':
        return 'warning';
      case 'not_configured':
        return 'error';
      default:
        return 'info';
    }
  };

  const getOverallStatusMessage = (status: string): string => {
    switch (status) {
      case 'fully_configured':
        return 'All services are configured and connected';
      case 'operational':
        return 'All required services configured. Optional services available.';
      case 'partial':
        return 'Some required services need configuration';
      case 'not_configured':
        return 'Critical services not configured';
      default:
        return 'Unknown status';
    }
  };

  const getStatusChip = (service: ServiceStatus) => {
    if (service.configured && service.connected) {
      return (
        <Chip
          icon={<CheckIcon />}
          label="Connected"
          color="success"
          size="small"
          variant="outlined"
        />
      );
    } else if (service.configured && !service.connected) {
      return (
        <Chip
          icon={<WarningIcon />}
          label="Configured"
          color="warning"
          size="small"
          variant="outlined"
        />
      );
    } else {
      return (
        <Chip
          icon={<CancelIcon />}
          label="Not Configured"
          color="error"
          size="small"
          variant="outlined"
        />
      );
    }
  };

  if (loading && !serviceStatus) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Format relative time
  const getRelativeTime = (date: Date): string => {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    if (seconds < 5) return 'just now';
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  return (
    <Box>
      {/* Action Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Monitor service connectivity and configuration status
          </Typography>
          {lastUpdated && (
            <Chip
              size="small"
              label={`Updated ${getRelativeTime(lastUpdated)}`}
              color={loading ? 'default' : 'success'}
              variant="outlined"
              sx={{ fontSize: '0.75rem' }}
            />
          )}
        </Box>
        <Tooltip title="Refresh (auto-updates every 30s)">
          <IconButton onClick={fetchServiceStatus} color="primary" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : <RefreshIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {serviceStatus && (
        <>
          {/* Summary Card */}
          <Alert
            severity={getOverallStatusColor(serviceStatus.overall_status)}
            sx={{ mb: 3 }}
          >
            <Typography variant="h6">
              Status: {serviceStatus.overall_status.replace('_', ' ').toUpperCase()}
            </Typography>
            <Typography variant="body2">
              {getOverallStatusMessage(serviceStatus.overall_status)}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Required: {serviceStatus.required_configured}/{serviceStatus.required_total} |
              Total: {serviceStatus.configured_count}/{serviceStatus.total_count} services configured
            </Typography>
          </Alert>

          {/* Progress Bar */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Configuration Progress
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={(serviceStatus.configured_count / serviceStatus.total_count) * 100}
                    color={
                      serviceStatus.configured_count === serviceStatus.total_count
                        ? 'success'
                        : serviceStatus.required_configured === serviceStatus.required_total
                        ? 'info'
                        : 'warning'
                    }
                    sx={{ height: 10, borderRadius: 5 }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {Math.round((serviceStatus.configured_count / serviceStatus.total_count) * 100)}%
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Service Grid */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {Object.entries(serviceStatus.services).map(([key, service]) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={key}>
                <Card
                  sx={{
                    height: '100%',
                    borderLeft: 4,
                    borderColor: service.configured && service.connected
                      ? 'success.main'
                      : service.configured
                      ? 'warning.main'
                      : 'error.main',
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box sx={{ color: 'text.secondary' }}>
                        {serviceIcons[key] || <CloudIcon />}
                      </Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {service.name}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 1 }}>
                      {getStatusChip(service)}
                      {service.required && (
                        <Chip
                          label="Required"
                          size="small"
                          sx={{ ml: 1 }}
                          color="primary"
                          variant="outlined"
                        />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {service.message}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Detailed Table */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Service Details
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Service</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Required</TableCell>
                      <TableCell>Configured</TableCell>
                      <TableCell>Connected</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(serviceStatus.services).map(([key, service]) => (
                      <TableRow
                        key={key}
                        sx={{
                          backgroundColor: !service.configured && service.required
                            ? 'error.50'
                            : 'inherit',
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ color: 'text.secondary', display: 'flex' }}>
                              {serviceIcons[key] || <CloudIcon fontSize="small" />}
                            </Box>
                            <Typography variant="body2" fontWeight={500}>
                              {service.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{getStatusChip(service)}</TableCell>
                        <TableCell>
                          {service.required ? (
                            <Chip label="Yes" size="small" color="primary" variant="outlined" />
                          ) : (
                            <Chip label="No" size="small" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell>
                          {service.configured ? (
                            <CheckIcon color="success" fontSize="small" />
                          ) : (
                            <CancelIcon color="error" fontSize="small" />
                          )}
                        </TableCell>
                        <TableCell>
                          {service.connected ? (
                            <CheckIcon color="success" fontSize="small" />
                          ) : (
                            <CancelIcon color="error" fontSize="small" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {service.message}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default ServiceConfigStatus;
